import * as React from "react";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import { Content } from "./styles";
import { usePanelContext } from "./PanelContext";
import { cn } from "./PanelShared";
import type { TypePanelContentProps } from "./PanelTypes";

/**
 * Styled-components content — the backward-compatible fallback. Routed to when
 * the consumer passes styled-system props (e.g. `<Panel.Content color="...">`).
 */
const PanelContentStyled = ({ children, ...rest }: TypePanelContentProps) => {
  // In headerless mode the content renders flush as a flex column and defers
  // overflow scrolling to its children, so the panel can host content that
  // owns its own chrome and fills the panel height (e.g. via flex: 1 1 auto).
  const { headerless } = usePanelContext();

  return (
    <Content
      flex="1 1 auto"
      minHeight="0"
      p={headerless ? 0 : 450}
      $scroll={!headerless}
      color="text.body"
      data-panel-content=""
      {...rest}
    >
      {children}
    </Content>
  );
};

/**
 * Tailwind/CSS-class content — the preferred path. Renders a plain
 * `<div data-panel-content>` with the `seeds-panel-content` class from
 * panel.css. The scroll behaviour depends on `headerless` (read from context)
 * and is asserted by the jsdom tests, so it is applied inline rather than via
 * the CSS class (panel.css is not loaded under jsdom).
 */
const PanelContentTailwind = ({ children, ...rest }: TypePanelContentProps) => {
  const { headerless } = usePanelContext();
  const scroll = !headerless;

  const { className, style, ...domRest } = rest as {
    className?: string;
    style?: React.CSSProperties;
  } & Record<string, unknown>;

  const contentStyle: React.CSSProperties = {
    overflowY: scroll ? "auto" : "hidden",
  };
  // When content owns its own scrolling (headerless), lay out as a flex column
  // so a child can flex to the panel's full height.
  if (!scroll) {
    contentStyle.display = "flex";
    contentStyle.flexDirection = "column";
  }
  Object.assign(contentStyle, style);

  return (
    <div
      className={cn(
        "seeds-panel-content",
        { "seeds-panel-content--headerless": !!headerless },
        className
      )}
      style={contentStyle}
      data-panel-content=""
      {...domRest}
    >
      {children}
    </div>
  );
};

/**
 * Hybrid content router. Routes on the leftover props after the content's own
 * props are removed.
 */
export const PanelContent = (props: TypePanelContentProps) => {
  const { children, ...rest } = props;

  if (needsStyledComponents(rest)) {
    return <PanelContentStyled {...props} />;
  }

  return <PanelContentTailwind {...props} />;
};

PanelContent.displayName = "Panel.Content";
